"use client"

import { useEffect, useCallback, useState, useMemo } from "react"
import errorToast from "../../../shared/lib/toast/error.toast"
import HomeOwnersTable from "./home-owners-table"
import HomeOwnersResultsCount from "./home-owners-results-count"
import HomeOwnersFilterIndicator from "./home-owners-filter-indicator"
import { PaginationData, usePaginationData } from "../context/pagination-data-context"
import { useFilterSearchHomes, FilterSearchHomesResponse, type FilterSearchHome } from "../hooks/filter-search-homes.hook"
import { getGoodwillStatusForSlug } from "../lib/workscope-goodwill-filter"
import { useFilterSearchExport } from "../context/filter-search-export-context"
import { useSearchParams, useRouter, usePathname } from "next/navigation"
import { useSession } from "@/features/auth/context/session.context"
import { useRegionId } from "@/shared/hooks/use-region-id"
import { useRegion } from "@/shared/hooks/use-region"
import { useRegionAwareHref } from "@/shared/hooks/use-region-aware-href"
import { DASHBOARD_ROUTE } from "@/shared/constants/pages-routes"
import { normalizeOverallStatus, normalizeOverallStatusList } from "../lib/overall-status.utils"
import { computeReliefStatus, computeRebuildingStatus } from "../lib/relief-rebuilding-status"
import { UserRole } from "@/shared/constants/user-roles"
import { normalizeClaimedByForDisplay } from "../lib/claimed-by-display"

function normalizeClaimedBy(raw: unknown): string[] {
    if (raw == null) return []
    if (Array.isArray(raw)) return raw.flatMap(normalizeClaimedBy)
    if (typeof raw !== "string") return []

    const value = raw.trim()
    if (!value) return []

    try {
        return normalizeClaimedBy(JSON.parse(value))
    } catch {
        return value.split(",").map((item) => item.trim()).filter(Boolean)
    }
}

function getClaimedByValues(home: unknown): string[] {
    if (!home || typeof home !== "object") return []

    return Object.entries(home as Record<string, unknown>).flatMap(([key, value]) => {
        const directValues = key.toLowerCase().includes("claimedby") ? normalizeClaimedBy(value) : []
        const nestedValues = value && typeof value === "object" ? getClaimedByValues(value) : []
        return [...directValues, ...nestedValues]
    })
}

/**
 * HomeOwnersListPreloaded component
 * Uses preloaded filter-search data instead of paginated API calls
 * Implements client-side pagination and filtering
 */
function HomeOwnersListPreloaded({ hideOwnerDetails = false, hideFlowchartLinks = false, mapRoute, workscopeParam, workscopeStatusParam }: { hideOwnerDetails?: boolean, hideFlowchartLinks?: boolean, mapRoute?: string, workscopeParam?: string, workscopeStatusParam?: string } = {}) {
    // Get pagination context setter
    const { setPaginationData } = usePaginationData()
    const { setFilteredHomesForExport } = useFilterSearchExport()
    const searchParams = useSearchParams()
    const router = useRouter()
    const pathname = usePathname()
    const { session } = useSession()
    const isVolunteer = session?.role === UserRole.VOLUNTEER
    const { regionId, isLoading: regionLoading } = useRegionId()
    const { regionSlug } = useRegion()
    const defaultMapRoute = useRegionAwareHref(mapRoute ?? DASHBOARD_ROUTE.map)
    // Always read from URL so desktop table stays in sync with filter bar (single source of truth)
    const workscope = (searchParams.get("workscope") ?? "").trim()
    const claimedBy = (searchParams.get("claimedBy") ?? "").trim()
    const workscopeStatus = (searchParams.get("workscopeStatus") ?? "").trim()
    const workscopeGoodwill = (searchParams.get("workscopeGoodwill") ?? "").trim()
    const workscopeGoodwillStatus = (searchParams.get("workscopeGoodwillStatus") ?? "").trim()

    // Normalize workscopeGoodwill from URL: "exterior debris removal goodwill" -> "exterior-debris-removal-goodwill"
    const workscopeGoodwillSlugs = useMemo(() => {
        if (!workscopeGoodwill) return []
        return workscopeGoodwill.split(',')
            .map((s) => s.trim().toLowerCase().replace(/\s+/g, '-'))
            .filter(Boolean)
    }, [workscopeGoodwill])

    // Get all homes data from filter-search API.
    // Start fetching as soon as we have regionSlug (from URL); no need to wait for regionId.
    const shouldFetchHomes = (regionSlug != null && regionSlug.trim() !== '') || (!regionLoading && regionId != null)
    const { data: allHomesData, isLoading: isLoadingAll } = useFilterSearchHomes(
        regionId ?? undefined,
        true,
        {
            enabled: shouldFetchHomes,
            regionSlug: regionSlug ?? undefined,
            workscope: workscope || undefined,
            workscopeStatus: workscopeStatus || undefined,
            workscopeGoodwill: workscopeGoodwillSlugs.length > 0 ? workscopeGoodwillSlugs.join(',') : undefined,
            workscopeGoodwillStatus: workscopeGoodwillStatus || undefined,
            initialLimit: 50,
        }
    )

    // When backend filter params are set, only use data after the filtered request has finished
    const apiAlreadyFilteredByGoodwill = workscopeGoodwillSlugs.length > 0 || (workscopeGoodwillStatus && workscopeGoodwillStatus.trim().length > 0)
    const effectiveHomesData = (workscope || workscopeStatus || apiAlreadyFilteredByGoodwill) && isLoadingAll ? undefined : allHomesData
    
    // Client-side pagination state
    const [pageSize] = useState(50) // Default page size
    
    // Get pagination and filter parameters from URL
    const currentPage = parseInt(searchParams.get("page") || "1")
    const search = searchParams.get("search") || ""
    const homeownerName = searchParams.get("homeownerName") || ""
    const city = searchParams.get("city") || ""
    const county = searchParams.get("county") || ""
    const street = searchParams.get("street") || ""
    const overallStatus = searchParams.get("overallStatus") || ""
    const reliefStatus = searchParams.get("reliefStatus") || ""
    const rebuildingStatus = searchParams.get("rebuildingStatus") || ""
    const goodwillStatus = searchParams.get("goodwillStatus") || ""
    const roofing = searchParams.get("roofing") || ""
    const kindOfRoofWork = searchParams.get("kindOfRoofWork") || ""
    const medicalNeeds = isVolunteer ? "" : (searchParams.get("medicalNeeds") || "")
    const homeownerAge = isVolunteer ? "" : (searchParams.get("homeownerAge") || "")
    const isSingleParent = isVolunteer ? "" : (searchParams.get("isSingleParent") || "")
    const numChildrenUnder18 = isVolunteer ? "" : (searchParams.get("numChildrenUnder18") || "")
    const isFirstResponder = isVolunteer ? "" : (searchParams.get("isFirstResponder") || "")
    const isVeteran = isVolunteer ? "" : (searchParams.get("isVeteran") || "")
    const residenceType = searchParams.get("residenceType") || ""
    const structureType = searchParams.get("structureType") || ""
    const assessmentCompleted = searchParams.get("assessmentCompleted") || ""
    const updatedFrom = searchParams.get("updatedFrom") || ""
    const updatedTo = searchParams.get("updatedTo") || ""
    const addedFrom = searchParams.get("addedFrom") || ""
    const addedTo = searchParams.get("addedTo") || ""
    const activeStatus = searchParams.get("activeStatus") || ""

    // Filter and search the data client-side
    const filteredData = useMemo(() => {
        if (!effectiveHomesData) {
            return []
        }
        
        const response = effectiveHomesData as FilterSearchHomesResponse;
        if (!response.success || !response.data) {
            return []
        }

        // API was already called with workscope/workscopeStatus, so backend returned only matching homes.
        // Do not re-apply workscope/workscopeStatus filter here: the API response may not include those relation fields in the select, so we'd filter out all rows.
        const apiAlreadyFilteredByWorkscope = !!(workscope || workscopeStatus)
        const selectedClaimedBy = claimedBy.split(",").map(normalizeClaimedByForDisplay).filter(Boolean)

        // Debug: Log when filters are applied
        const hasFilters = search || homeownerName || city || county || street || overallStatus || reliefStatus || rebuildingStatus || goodwillStatus || roofing || kindOfRoofWork || workscope || claimedBy || workscopeStatus || workscopeGoodwill || workscopeGoodwillStatus || medicalNeeds || 
                          homeownerAge || isSingleParent || numChildrenUnder18 || isFirstResponder || 
                          isVeteran || residenceType || structureType || assessmentCompleted
        
        if (hasFilters) {
            console.log('All filters applied:', {
                search, homeownerName, city, street, overallStatus, roofing, kindOfRoofWork, workscope, claimedBy, workscopeStatus, workscopeGoodwill, workscopeGoodwillStatus, medicalNeeds, 
                homeownerAge, isSingleParent, numChildrenUnder18, isFirstResponder, 
                isVeteran, residenceType, structureType, assessmentCompleted
            })
        }

        return response.data.filter((home: any) => {
            // Search filter (name, address, phone, or specific home ID)
            if (search) {
                const searchTrim = search.trim()
                const isNumericSearch = /^\d+$/.test(searchTrim)
                const searchAsNumber = isNumericSearch ? parseInt(searchTrim, 10) : 0
                const treatAsHomeIdOnly = isNumericSearch && searchAsNumber > 1000
                // When search looks like a home ID (numeric and > 1000), only include that home—do not also match by text (e.g. "1587" in address)
                if (treatAsHomeIdOnly) {
                    return home.id.toString() === searchTrim
                }
                if (home.id.toString() === searchTrim) {
                    return true
                }
                const searchLower = searchTrim.toLowerCase()
                const name = home.homeowner?.user?.fullname?.toLowerCase() || ""
                const address = home.address?.toLowerCase() || ""
                const phone = home.homeowner?.user?.phone?.toLowerCase() || ""
                if (!name.includes(searchLower) &&
                    !address.includes(searchLower) &&
                    !phone.includes(searchLower)) {
                    return false
                }
            }

            // Filter by homeowner name (column filter)
            if (homeownerName) {
                const name = (home.homeowner?.user?.fullname || "").toLowerCase()
                if (!name.includes(homeownerName.toLowerCase())) {
                    return false
                }
            }

            // Filter by city (multi-select from Filter modal; comma-separated in URL)
            if (city) {
                const cityList = city.split(',').map((c: string) => (c || '').trim().toLowerCase()).filter(Boolean)
                if (cityList.length > 0) {
                    const homeCity = (home.city || '').trim().toLowerCase()
                    if (!homeCity || !cityList.includes(homeCity)) return false
                }
            }

            // Filter by county (multi-select from Filter modal; comma-separated in URL)
            if (county) {
                const countyList = county.split(',').map((c: string) => (c || '').trim().toLowerCase()).filter(Boolean)
                if (countyList.length > 0) {
                    const homeCounty = (home.county || '').trim().toLowerCase()
                    if (!homeCounty || !countyList.includes(homeCounty)) return false
                }
            }

            // Filter by street (column filter: matches address)
            if (street) {
                const homeAddress = (home.address || "").toLowerCase()
                if (!homeAddress.includes(street.toLowerCase())) {
                    return false
                }
            }

            // Filter by Active/Inactive (reported status)
            if (activeStatus) {
                const isActive = home.isReported === true
                if (activeStatus === "active" && !isActive) return false
                if (activeStatus === "inactive" && isActive) return false
            }

            // Filter by overall status (multi-select)
            if (overallStatus) {
                const statusArray = normalizeOverallStatusList(overallStatus.split(','))
                const homeStatus = normalizeOverallStatus(home.homeStatusInfo?.overallStatus)
                if (!statusArray.includes(homeStatus)) {
                    return false
                }
            }

            // Filter by derived relief / rebuilding status (same computation as Home Details; limited by filter-search payload fields)
            if (reliefStatus) {
                const statusArray = reliefStatus.split(',').map((s: string) => s.trim()).filter(Boolean)
                const label =
                    (home.reliefStatus as string | undefined) ??
                    computeReliefStatus({ home }).displayLabel
                if (!statusArray.includes(label)) {
                    return false
                }
            }
            if (rebuildingStatus) {
                const statusArray = rebuildingStatus.split(',').map((s: string) => s.trim()).filter(Boolean)
                const label =
                    (home.rebuildingStatus as string | undefined) ??
                    computeRebuildingStatus({ home }).displayLabel
                if (!statusArray.includes(label)) {
                    return false
                }
            }

            // Filter by goodwill status (multi-select)
            if (goodwillStatus) {
                const statusArray = goodwillStatus.split(',')
                const homeGoodwill = home.goodwillStatus ?? 'No Assistance Required'
                if (!statusArray.includes(homeGoodwill)) {
                    return false
                }
            }

            // Filter by Updated date range
            if (updatedFrom || updatedTo) {
                const u = home.updatedAt ? new Date(home.updatedAt).getTime() : null
                if (u == null) return false
                if (updatedFrom && /^\d{4}-\d{2}-\d{2}$/.test(updatedFrom)) {
                    const fromStart = new Date(updatedFrom + "T00:00:00").getTime()
                    if (u < fromStart) return false
                }
                if (updatedTo && /^\d{4}-\d{2}-\d{2}$/.test(updatedTo)) {
                    const toEnd = new Date(updatedTo + "T23:59:59.999").getTime()
                    if (u > toEnd) return false
                }
            }

            // Filter by Added date (when home was first added to DB)
            if (addedFrom || addedTo) {
                const created = home.createdAt ? new Date(home.createdAt).getTime() : null
                if (created == null) return false
                if (addedFrom && /^\d{4}-\d{2}-\d{2}$/.test(addedFrom)) {
                    const fromStart = new Date(addedFrom + "T00:00:00").getTime()
                    if (created < fromStart) return false
                }
                if (addedTo && /^\d{4}-\d{2}-\d{2}$/.test(addedTo)) {
                    const toEnd = new Date(addedTo + "T23:59:59.999").getTime()
                    if (created > toEnd) return false
                }
            }

            // Filter by roofing (multi-select)
            if (roofing) {
                const roofingArray = roofing.split(',')
                const homeRoofing = home.roofingInfo?.roofing
                if (!roofingArray.includes(homeRoofing)) {
                    return false
                }
            }

            // Filter by kind of roof work (multi-select)
            if (kindOfRoofWork) {
                const roofWorkArray = kindOfRoofWork.split(',')
                const homeRoofWork = home.roofingInfo?.kindOfRoofWork
                if (!roofWorkArray.includes(homeRoofWork)) {
                    return false
                }
            }

            // Filter by workscope (multi-select): home must have at least one of the selected work scopes "in scope" (status set and not "No Assistance Required")
            // Skip when API was already called with workscope/workscopeStatus (backend already filtered; response may not include relation fields)
            if (workscope && !apiAlreadyFilteredByWorkscope) {
                const workscopeSlugs = workscope.split(',').map((s: string) => s.trim().toLowerCase().replace(/\s+/g, '-').replace(/_/g, '-')).filter(Boolean)
                if (workscopeSlugs.length > 0) {
                    const getStatusForSlug = (slug: string, h: any): string | null | undefined => {
                        switch (slug) {
                            case 'assess-needs': return h.repairabilityInfo?.assessmentCompleted
                            case 'tree-work': return h.treeDebrisInfo?.treeWorkNeeded
                            case 'exterior-debris-removal': return h.treeDebrisInfo?.exteriorDebrisRemovalNeeded
                            case 'heavy-equipment-demo': return h.treeDebrisInfo?.heavyEquipmentNeeded
                            case 'tarp-roof': return h.roofingInfo?.roofTarpNeeded
                            case 'install-roofing': return h.roofingInfo?.roofing
                            case 'muck-and-gut-interior': return h.muckGutInfo?.muckGutNeeded
                            case 'mold-remediation': return h.moldRemediationInfo?.moldRemediation
                            case 'rebuild-prep': return h.rebuildPrepInfo?.rebuildPrep
                            case 'rough-carpentry': return h.roughCarpentryInfo?.roughCarpentry
                            case 'electrical-fixtures': return h.electricalInfo?.electrical
                            case 'rough-electrical': return h.electricalInfo?.electricalRoughIn
                            case 'hvac-final': return h.hvacInfo?.hvac
                            case 'rough-mechanical': return h.hvacInfo?.mechanicalRoughIn
                            case 'plumbing-fixtures': return h.plumbingInfo?.plumbing
                            case 'rough-plumbing': return h.plumbingInfo?.plumbingRoughIn
                            case 'insulation': return h.insulationInstallInfo?.insulationInstall
                            case 'drywall-hanging': return h.drywallHangingInfo?.drywallHanging
                            case 'mud-and-tape': return h.mudAndTapeInfo?.mudAndTape
                            case 'install-cabinets': return h.cabinetInstallInfo?.cabinetInstall
                            case 'install-counters': return h.cabinetInstallInfo?.counterInstall
                            case 'install-appliances': return h.cabinetInstallInfo?.applianceInstall
                            case 'bathroom-rebuild': return h.bathroomRebuildInfo?.bathroomRebuild
                            case 'interior-paint': return h.interiorPaintInfo?.interiorPaint
                            case 'flooring-install': return h.flooringInstallInfo?.flooringInstall
                            case 'install-interior-doors': return h.installInteriorDoorsInfo?.installInteriorDoors
                            case 'trim-install': return h.trimInstallInfo?.trimInstall
                            case 'install-exterior-doors': return h.exteriorDoorInstallInfo?.exteriorDoorInstall
                            case 'm-and-g-crawl-space': return h.crawlSpaceInfo?.crawlSpaceMuck
                            case 'insulate-crawl-space': return h.crawlSpaceInfo?.crawlSpaceInsulation
                            case 'misc': return h.miscellaneousInfo?.miscellaneous
                            case 'shutter-install': return h.miscellaneousInfo?.shutterInstall
                            case 'repair-driveway': return h.miscellaneousInfo?.drivewayRepair
                            default: return undefined
                        }
                    }
                    const hasMatchingWorkscope = workscopeSlugs.some((slug) => {
                        const status = getStatusForSlug(slug, home)
                        return status != null && status !== '' && status !== 'No Assistance Required'
                    })
                    if (!hasMatchingWorkscope) {
                        return false
                    }
                    // When Status of Workscope is also selected, require the home's workscope status to be in the selected statuses
                    if (workscopeStatus) {
                        const statusList = workscopeStatus.split(',').map((s: string) => s.trim()).filter(Boolean)
                        if (statusList.length > 0) {
                            const hasMatchingStatus = workscopeSlugs.some((slug) => {
                                const status = getStatusForSlug(slug, home)
                                return status != null && statusList.includes(status)
                            })
                            if (!hasMatchingStatus) {
                                return false
                            }
                        }
                    }
                }
            }

            // Filter by workscope goodwill (multiselect): only when NOT already filtered by API
            if (workscopeGoodwillSlugs.length > 0 && !apiAlreadyFilteredByGoodwill) {
                const statusList = workscopeGoodwillStatus
                    ? workscopeGoodwillStatus.split(',').map((s: string) => s.trim()).filter(Boolean)
                    : []
                const hasMatchingGoodwill = workscopeGoodwillSlugs.some((slug) => {
                    const status = getGoodwillStatusForSlug(home, slug)
                    if (status == null) return false
                    if (statusList.length > 0) return statusList.includes(status)
                    return true
                })
                if (!hasMatchingGoodwill) {
                    return false
                }
            }

            // When API already filtered by goodwill, no client-side goodwill filter (backend did it)

            if (selectedClaimedBy.length > 0) {
                const homeClaimedByValues = getClaimedByValues(home).map(normalizeClaimedByForDisplay)
                if (!homeClaimedByValues.some((value) => selectedClaimedBy.includes(value))) {
                    return false
                }
            }

            // Filter by medical needs (any value = yes, empty/null/no = no)
            if (medicalNeeds) {
                const rawValue = home.homeowner?.medicalNeeds
                const medicalNeedsValue = rawValue?.trim().toLowerCase()
                
                // hasMedicalNeeds = true only if there's a value AND it's not empty AND it's not "no"
                const hasMedicalNeeds = rawValue !== null && 
                                       rawValue !== undefined && 
                                       medicalNeedsValue && 
                                       medicalNeedsValue !== '' && 
                                       medicalNeedsValue !== 'no'
                
                const expectedHasMedicalNeeds = medicalNeeds === 'yes'
                
                if (hasMedicalNeeds !== expectedHasMedicalNeeds) {
                    if (hasFilters) console.log('Medical needs filter failed:', { 
                        filter: medicalNeeds, 
                        expected: expectedHasMedicalNeeds, 
                        actual: hasMedicalNeeds,
                        rawValue: rawValue,
                        processedValue: medicalNeedsValue
                    })
                    return false
                }
            }

            // Filter by homeowner age (handle age ranges)
            if (homeownerAge && home.homeowner?.homeownerAge) {
                const actualAge = parseInt(home.homeowner.homeownerAge)
                const ageRange = homeownerAge.split(' - ')
                const minAge = parseInt(ageRange[0])
                const maxAge = parseInt(ageRange[1])
                
                if (actualAge < minAge || actualAge > maxAge) {
                    if (hasFilters) console.log('Homeowner age filter failed:', { filter: homeownerAge, actual: home.homeowner?.homeownerAge, actualAge, minAge, maxAge })
                    return false
                }
            }

            // Filter by single parent
            if (isSingleParent && home.homeowner?.isSingleParent !== isSingleParent) {
                if (hasFilters) console.log('Single parent filter failed:', { filter: isSingleParent, actual: home.homeowner?.isSingleParent })
                return false
            }

            // Filter by children under 18 (any number > 0 = yes, 0/null = no)
            if (numChildrenUnder18) {
                const actualChildren = home.homeowner?.numChildrenUnder18
                const hasChildren = actualChildren !== null && 
                                   actualChildren !== undefined && 
                                   Number(actualChildren) > 0
                const expectedHasChildren = numChildrenUnder18 === 'yes'
                
                if (hasChildren !== expectedHasChildren) {
                    if (hasFilters) console.log('Children under 18 filter failed:', { 
                        filter: numChildrenUnder18, 
                        expected: expectedHasChildren, 
                        actual: hasChildren,
                        actualChildren: actualChildren,
                        actualNum: Number(actualChildren)
                    })
                    return false
                }
            }

            // Filter by first responder
            if (isFirstResponder && home.homeowner?.isFirstResponder !== isFirstResponder) {
                if (hasFilters) console.log('First responder filter failed:', { filter: isFirstResponder, actual: home.homeowner?.isFirstResponder })
                return false
            }

            // Filter by veteran
            if (isVeteran && home.homeowner?.isVeteran !== isVeteran) {
                if (hasFilters) console.log('Veteran filter failed:', { filter: isVeteran, actual: home.homeowner?.isVeteran })
                return false
            }


            // Filter by resident type (multi-select)
            if (residenceType) {
                const residenceTypeArray = residenceType.split(',')
                const homeResidenceType = home.repairabilityInfo?.residenceType
                if (!homeResidenceType || !residenceTypeArray.includes(homeResidenceType)) {
                    return false
                }
            }

            // Filter by structure type (multi-select)
            if (structureType) {
                const structureTypeArray = structureType.split(',')
                const homeStructureType = home.repairabilityInfo?.structureType
                if (!homeStructureType || !structureTypeArray.includes(homeStructureType)) {
                    return false
                }
            }

            // Filter by assessment completed (multi-select)
            if (assessmentCompleted) {
                const assessmentArray = assessmentCompleted.split(',')
                const homeAssessment = home.repairabilityInfo?.assessmentCompleted
                if (!assessmentArray.includes(homeAssessment)) {
                    return false
                }
            }

            return true
        })
    }, [effectiveHomesData, search, homeownerName, city, county, street, activeStatus, overallStatus, reliefStatus, rebuildingStatus, goodwillStatus, roofing, kindOfRoofWork, workscope, claimedBy, workscopeStatus, workscopeGoodwill, workscopeGoodwillSlugs, workscopeGoodwillStatus, apiAlreadyFilteredByGoodwill, medicalNeeds, homeownerAge, isSingleParent, numChildrenUnder18, isFirstResponder, isVeteran, residenceType, structureType, assessmentCompleted, updatedFrom, updatedTo, addedFrom, addedTo])

    // Expose filtered data for Download (Homeowners/FilterSearch export)
    useEffect(() => {
        setFilteredHomesForExport(filteredData)
        return () => setFilteredHomesForExport(null)
    }, [filteredData, setFilteredHomesForExport])

    // Unique homeowner names from the full dataset (for combobox suggestions), with userId for own-data masking
    const homeownerNames = useMemo(() => {
        if (!effectiveHomesData) return []
        const response = effectiveHomesData as FilterSearchHomesResponse
        if (!response.success || !response.data) return []
        const byName = new Map<string, number>()
        response.data.forEach((home: any) => {
            const n = home.homeowner?.user?.fullname?.trim()
            const uid = home.homeowner?.user?.id
            if (n && !byName.has(n)) byName.set(n, uid)
        })
        return Array.from(byName.entries())
            .map(([name, userId]) => ({ name, userId }))
            .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }))
    }, [effectiveHomesData])

    // Calculate pagination
    const totalItems = filteredData.length
    const totalPages = Math.ceil(totalItems / pageSize)
    const startIndex = (currentPage - 1) * pageSize
    const endIndex = startIndex + pageSize
    const paginatedData = filteredData.slice(startIndex, endIndex)

    // Transform data to match the expected format for HomeOwnersTable
    const transformedData = useMemo(() => {
        return paginatedData.map((home: any) => ({
            id: home.homeowner?.id || home.id,
            primaryLanguage: "English", // Default value
            secondaryContact: null,
            secondaryPhone: null,
            homeownerAge: home.homeowner?.homeownerAge,
            residentType: home.homeowner?.residentType,
            medicalNeeds: home.homeowner?.medicalNeeds,
            numChildrenUnder18: home.homeowner?.numChildrenUnder18,
            isFirstResponder: home.homeowner?.isFirstResponder,
            isSingleParent: home.homeowner?.isSingleParent,
            isVeteran: home.homeowner?.isVeteran,
            residenceType: home.repairabilityInfo?.residenceType,
            appliedFEMA: null,
            femaStatus: null,
            appliedSBA: null,
            sbaStatus: null,
            safetyConcerns: null,
            prayerRequest: null,
            ownerStory: null,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            user: home.homeowner?.user,
            home: {
                id: home.id,
                address: home.address,
                latitude: home.latitude || null,
                longitude: home.longitude || null,
                createdAt: home.createdAt ?? null,
                updatedAt: home.updatedAt ?? null,
                currentStep: 1,
                isReported: home.isReported,
                // Add other required home properties with defaults
                city: home.city || "",
                state: "",
                zipCode: "",
                structureType: home.structureType || null,
                yearBuilt: "",
                squareFootage: "",
                numberOfBedrooms: null,
                numberOfBathrooms: null,
                hasBasement: null,
                hasAttic: null,
                hasGarage: null,
                hasPool: null,
                hasDeck: null,
                hasPatio: null,
                hasFence: null,
                hasShed: null,
                hasWorkshop: null,
                hasGreenhouse: null,
                hasBarn: null,
                hasStable: null,
                hasPond: null,
                hasGarden: null,
                hasOrchard: null,
                hasVineyard: null,
                hasFarm: null,
                hasRanch: null,
                hasCabin: null,
                hasTrailer: null,
                hasMobileHome: null,
                hasRV: null,
                hasBoat: null,
                hasPlane: null,
                hasHelicopter: null,
                hasOther: null,
                otherDescription: null,
                notes: null,
                projectGeneralNotes: "",
                county: "",
                dogsInHome: null,
                shedNeeded: null,
                shedSize: null,
                shedLocation: null,
                shedPurpose: null,
                shedMaterials: null,
                shedCost: null,
                shedTimeline: null,
                shedPermits: null,
                shedContractor: null,
                shedInsurance: null,
                shedMaintenance: null,
                shedAccess: null,
                shedSecurity: null,
                shedLighting: null,
                shedElectrical: null,
                shedPlumbing: null,
                shedHeating: null,
                shedCooling: null,
                shedVentilation: null,
                shedInsulation: null,
                shedFlooring: null,
                shedWalls: null,
                shedCeiling: null,
                shedRoof: null,
                shedDoors: null,
                shedWindows: null,
                hasFloodInsurance: null,
                hasHomeInsurance: null,
                googlePicLink: null,
                homeowner: home.homeowner || null,
                homeStatusInfo: home.homeStatusInfo || null,
                goodwillStatus: home.goodwillStatus ?? 'No Assistance Required',
                reliefStatus:
                    (home as FilterSearchHome).reliefStatus ??
                    computeReliefStatus({ home }).displayLabel,
                rebuildingStatus:
                    (home as FilterSearchHome).rebuildingStatus ??
                    computeRebuildingStatus({ home }).displayLabel,
                roofingInfo: home.roofingInfo || null,
                repairabilityInfo: home.repairabilityInfo || null,
                workOrders: null,
                photos: null,
                documents: null,
                inspections: null,
                permits: null,
                contractors: null,
                materials: null,
                equipment: null,
                volunteers: null,
                donations: null,
                expenses: null,
                timeline: null,
                milestones: null,
                tasks: null,
                updates: null,
                communications: null,
                meetings: null,
                decisions: null,
                approvals: null,
                rejections: null,
                changes: null,
                revisions: null,
                treeDebrisInfo: {} as any,
                muckGutInfo: {} as any,
                moldRemediationInfo: {} as any,
                rebuildPrepInfo: {} as any,
                electricalInfo: {} as any,
                plumbingInfo: {} as any,
                hvacInfo: {} as any,
                flooringInfo: {} as any,
                drywallInfo: {} as any,
                paintingInfo: {} as any,
                landscapingInfo: {} as any,
                fencingInfo: {} as any,
                drivewayInfo: {} as any,
                sidewalkInfo: {} as any,
                foundationInfo: {} as any,
                basementInfo: {} as any,
                atticInfo: {} as any,
                garageInfo: {} as any,
                roughCarpentryInfo: {} as any,
                bathroomRebuildInfo: {} as any,
                crawlSpaceInfo: {} as any,
                insulationInstallInfo: {} as any,
                drywallInstallInfo: {} as any,
                paintingPrepInfo: {} as any,
                flooringInstallInfo: {} as any,
                trimWorkInfo: {} as any,
                finalCleanupInfo: {} as any,
                inspectionInfo: {} as any,
                permitInfo: {} as any,
                drywallHangingInfo: {} as any,
                mudAndTapeInfo: {} as any,
                cabinetInstallInfo: {} as any,
                interiorPaintInfo: {} as any,
                exteriorPaintInfo: {} as any,
                hardwareInstallInfo: {} as any,
                finalInspectionInfo: {} as any,
                installInteriorDoorsInfo: {} as any,
                trimInstallInfo: {} as any,
                exteriorDoorInstallInfo: {} as any,
                drivewayRepair: home.drivewayRepair || null,
                stormShutters: home.stormShutters || null,
                miscellaneousInfo: {} as any
            }
        }))
    }, [paginatedData])

    const updatedPaginatedData = useCallback((data: PaginationData) => {
        setPaginationData({
            page: data.page,
            limit: data.limit,
            total: data.total
        })
    }, [setPaginationData])

    const handleOverallStatusFilter = useCallback(
        (statuses: string[] | null) => {
            const params = new URLSearchParams(searchParams.toString())
            if (!statuses || statuses.length === 0) {
                params.delete("overallStatus")
            } else {
                params.set("overallStatus", statuses.join(","))
            }
            params.set("page", "1")
            router.push(`${pathname}?${params.toString()}`)
        },
        [pathname, router, searchParams]
    )

    const handleGoodwillStatusFilter = useCallback(
        (statuses: string[] | null) => {
            const params = new URLSearchParams(searchParams.toString())
            if (!statuses || statuses.length === 0) {
                params.delete("goodwillStatus")
            } else {
                params.set("goodwillStatus", statuses.join(","))
            }
            params.set("page", "1")
            router.push(`${pathname}?${params.toString()}`)
        },
        [pathname, router, searchParams]
    )

    const handleReliefStatusFilter = useCallback(
        (statuses: string[] | null) => {
            const params = new URLSearchParams(searchParams.toString())
            if (!statuses || statuses.length === 0) {
                params.delete("reliefStatus")
            } else {
                params.set("reliefStatus", statuses.join(","))
            }
            params.set("page", "1")
            router.push(`${pathname}?${params.toString()}`)
        },
        [pathname, router, searchParams]
    )

    const handleRebuildingStatusFilter = useCallback(
        (statuses: string[] | null) => {
            const params = new URLSearchParams(searchParams.toString())
            if (!statuses || statuses.length === 0) {
                params.delete("rebuildingStatus")
            } else {
                params.set("rebuildingStatus", statuses.join(","))
            }
            params.set("page", "1")
            router.push(`${pathname}?${params.toString()}`)
        },
        [pathname, router, searchParams]
    )

    const handleHomeownerNameFilter = useCallback(
        (value: string | null) => {
            const params = new URLSearchParams(searchParams.toString())
            if (value?.trim()) {
                params.set("homeownerName", value.trim())
            } else {
                params.delete("homeownerName")
            }
            params.set("page", "1")
            router.push(`${pathname}?${params.toString()}`)
        },
        [pathname, router, searchParams]
    )

    const handleAddressFilter = useCallback(
        ({ city: c, street: s }: { city: string | null; street: string | null }) => {
            const params = new URLSearchParams(searchParams.toString())
            if (c?.trim()) {
                params.set("city", c.trim())
            } else {
                params.delete("city")
            }
            if (s?.trim()) {
                params.set("street", s.trim())
            } else {
                params.delete("street")
            }
            params.set("page", "1")
            router.push(`${pathname}?${params.toString()}`)
        },
        [pathname, router, searchParams]
    )

    const handleUpdatedFilter = useCallback(
        (from: string | null, to: string | null) => {
            const params = new URLSearchParams(searchParams.toString())
            if (from?.trim()) params.set("updatedFrom", from.trim())
            else params.delete("updatedFrom")
            if (to?.trim()) params.set("updatedTo", to.trim())
            else params.delete("updatedTo")
            params.set("page", "1")
            router.push(`${pathname}?${params.toString()}`)
        },
        [pathname, router, searchParams]
    )

    // Update pagination context when data changes
    useEffect(() => {
        updatedPaginatedData({
            page: currentPage,
            limit: pageSize,
            total: totalItems
        })
    }, [currentPage, pageSize, totalItems, updatedPaginatedData])

    // Note: Page reset when filters change is now handled by the pagination component
    // which updates the URL, and this component reads from the URL

    // Handle error states
    if (allHomesData) {
        const response = allHomesData as FilterSearchHomesResponse
        if (!response.success) {
            errorToast(response.message || "Something went wrong while fetching home owners")
            return null
        }
    }

    // Combine loading states - show loading if region is loading OR data is loading
    const isLoading = regionLoading || isLoadingAll

    // Render the home owners table component
    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-baseline gap-x-4 gap-y-2">
                <HomeOwnersResultsCount 
                    total={totalItems} 
                    isLoading={isLoading} 
                />
                <HomeOwnersFilterIndicator />
            </div>
            <HomeOwnersTable 
                data={transformedData} 
                isLoading={isLoading}
                hideOwnerDetails={hideOwnerDetails}
                hideFlowchartLinks={hideFlowchartLinks}
                mapRoute={defaultMapRoute}
                userRole={session?.role || null}
                sessionSub={session?.sub ?? null}
                overallStatusFilter={overallStatus}
                onOverallStatusFilter={handleOverallStatusFilter}
                reliefStatusFilter={reliefStatus}
                onReliefStatusFilter={handleReliefStatusFilter}
                rebuildingStatusFilter={rebuildingStatus}
                onRebuildingStatusFilter={handleRebuildingStatusFilter}
                goodwillStatusFilter={goodwillStatus}
                onGoodwillStatusFilter={handleGoodwillStatusFilter}
                homeownerNameFilter={homeownerName}
                onHomeownerNameFilter={handleHomeownerNameFilter}
                homeownerNames={homeownerNames}
                cityFilter={city}
                streetFilter={street}
                onAddressFilter={handleAddressFilter}
                updatedFromFilter={updatedFrom}
                updatedToFilter={updatedTo}
                onUpdatedFilter={handleUpdatedFilter}
            />
        </div>
    )
}

export default HomeOwnersListPreloaded
